import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MaskHeading, Reveal, EASE } from "@/components/site/motion";
import { BUSINESS } from "@/config/businessConfig";

// Approximate relative positions (not to scale) around Woodstock.
const PINS = [
  { n: "Woodstock", x: 50, y: 46, main: true },
  { n: "Ingersoll", x: 31, y: 63, main: true },
  { n: "Tillsonburg", x: 48, y: 88 },
  { n: "Norwich", x: 62, y: 74 },
  { n: "Tavistock", x: 50, y: 12 },
  { n: "Embro", x: 30, y: 32 },
  { n: "Thamesford", x: 15, y: 50 },
  { n: "Innerkip", x: 64, y: 30 },
  { n: "Beachville", x: 40, y: 56 },
  { n: "Drumbo", x: 82, y: 38 },
  { n: "Plattsville", x: 78, y: 16 },
];

const MapViz = () => (
  <div className="relative aspect-square w-full max-w-[560px] mx-auto bg-[var(--char)] border border-[var(--line)] blueprint overflow-hidden" data-testid="service-area-map">
    {[22, 40, 58].map((r, i) => (
      <motion.div key={r} initial={{ scale: 0.6, opacity: 0 }} whileInView={{ scale: 1, opacity: 1 }} viewport={{ once: true, amount: 0.1 }}
        transition={{ duration: 1.2, ease: EASE, delay: i * 0.15 }}
        className="absolute rounded-full border border-dashed border-[var(--orange)]/40"
        style={{ width: `${r * 2}%`, height: `${r * 2}%`, left: `${50 - r}%`, top: `${46 - r}%` }} />
    ))}
    {PINS.map((p, i) => (
      <motion.div key={p.n} initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }}
        transition={{ duration: 0.6, ease: EASE, delay: 0.4 + i * 0.06 }}
        className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center" style={{ left: `${p.x}%`, top: `${p.y}%` }}>
        <span className="relative flex">
          {p.main && <span className="absolute inset-0 rounded-full bg-[var(--orange)] ping-soft" />}
          <span className={`relative rounded-full ${p.main ? "w-3.5 h-3.5 bg-[var(--orange)]" : "w-2 h-2 bg-white"}`} />
        </span>
        <span className={`mt-1.5 font-mono-x uppercase whitespace-nowrap ${p.main ? "text-[12px] text-white font-bold" : "text-[11px] text-zinc-300"}`}>{p.n}</span>
      </motion.div>
    ))}
    <span className="absolute bottom-3 left-3 font-mono-x text-[11px] text-zinc-400 uppercase tracking-widest">Oxford County, ON · not to scale</span>
  </div>
);

export const ServiceArea = () => (
  <section className="relative bg-[var(--char)] py-24 lg:py-36 border-t border-[var(--line)]" data-testid="service-area-section">
    <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 grid gap-14 lg:grid-cols-12 items-center">
      <div className="lg:col-span-6 order-2 lg:order-1"><MapViz /></div>
      <div className="lg:col-span-6 order-1 lg:order-2">
        <p className="eyebrow mb-5">Service area</p>
        <MaskHeading lines={[{ text: "Based in Woodstock." }, { text: "Working across", className: "text-zinc-400" }, { text: "Oxford County.", className: "text-[var(--orange)]" }]}
          className="font-display font-black uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9]" />
        <Reveal>
          <p className="mt-7 text-zinc-300 text-base lg:text-lg leading-relaxed max-w-xl">
            We serve Woodstock, Ingersoll and most of Oxford County. Not sure if we reach your town? Send a quote request — we'll let you know.
          </p>
          <ul className="mt-8 flex flex-wrap gap-2" data-testid="service-towns">
            {BUSINESS.serviceTowns.map((t, i) => (
              <li key={t} className={`px-3.5 py-2 font-mono-x text-xs uppercase tracking-wider border ${i < 2 ? "bg-[var(--orange)] text-[var(--ink)] border-[var(--orange)] font-bold" : "border-[var(--line)] text-zinc-300"}`}>{t}</li>
            ))}
          </ul>
          <Link to="/contact" data-testid="area-quote-btn" className="btn-orange mt-10 px-7 py-4 text-lg"><span>Check my area</span><ArrowUpRight className="w-5 h-5" /></Link>
        </Reveal>
      </div>
    </div>
  </section>
);
